import {
  EmbeddedAuthError,
  type EmbeddedAuthTokenResult,
  MsalPopupAuthService,
} from '@/platform/authentication/MsalPopupAuthService';
import { sessionManager } from '@/platform/authentication/SessionManager';
import { authConfig } from '@/config/authConfig';
import { normalizeEmbeddedProductId } from '@/platform/authentication/embeddedProductId';
import { authService } from '@/auth/services/AuthService';

function isExpiringSoon(expiresAt: string): boolean {
  const expiresAtMs = new Date(expiresAt).getTime();
  const nowMs = Date.now();
  const refreshWindowMs = 2 * 60 * 1000;

  return expiresAtMs - nowMs <= refreshWindowMs;
}

class TokenProvider {
  private readonly popupAuthService = new MsalPopupAuthService();

  // Standard OIDC scopes that are always allowed for any product
  private readonly standardOidcScopes = new Set([
    'openid',
    'profile',
    'email',
    'offline_access',
  ]);

  private readonly scopedProducts = new Map<string, string[]>([
    ['on-demand', authConfig.onDemandApiScopes],
    ['on-demand-portal', authConfig.onDemandApiScopes],
    ['global-data-editing-tool', authConfig.detApiScopes],
  ]);

  private resolveRequestedScopes(
    productId: string,
    scopes?: string[],
  ): string[] {
    if (!productId.trim()) {
      throw new EmbeddedAuthError(
        'unknown',
        'Embedded auth request is missing productId.',
        false,
      );
    }

    const allowlist =
      this.scopedProducts.get(productId) ?? authConfig.apiScopes;
    if (allowlist.length === 0) {
      throw new EmbeddedAuthError(
        'unknown',
        `No allowed scopes configured for product ${productId}.`,
        false,
      );
    }

    if (!scopes || scopes.length === 0) {
      return allowlist;
    }

    const requested = scopes.map((scope) => scope.trim()).filter(Boolean);
    const hasInvalid = requested.some(
      (scope) =>
        !allowlist.includes(scope) && !this.standardOidcScopes.has(scope),
    );

    if (hasInvalid) {
      throw new EmbeddedAuthError(
        'unknown',
        `Requested scopes are not allowed for product ${productId}.`,
        false,
      );
    }

    return requested;
  }

  async getTokenForProduct(
    productId: string,
    scopes?: string[],
    options: { interactive?: boolean } = {},
  ): Promise<EmbeddedAuthTokenResult> {
    const { interactive = true } = options;
    const normalizedProductId = normalizeEmbeddedProductId(productId);
    const resolvedScopes = this.resolveRequestedScopes(
      normalizedProductId,
      scopes,
    );
    const existing = sessionManager.getSession(normalizedProductId);

    if (
      existing &&
      !isExpiringSoon(existing.expiresAt) &&
      resolvedScopes.every((scope) => existing.scopes.includes(scope))
    ) {
      return existing;
    }

    // Get the active account from Entity Portal's main authentication service
    // This enables silent token acquisition for embedded products using the
    // existing Entity Portal session, avoiding unnecessary login popups.
    const account =
      authService.getActiveAccount() ??
      authService.getAllAccounts()[0] ??
      undefined;

    const tokenResult = await this.popupAuthService.acquireToken(
      resolvedScopes,
      account,
      {
        interactive,
      },
    );
    sessionManager.upsertSession(normalizedProductId, tokenResult);
    return tokenResult;
  }

  clearProductSession(productId: string): void {
    sessionManager.clearSession(normalizeEmbeddedProductId(productId));
  }
}

export const tokenProvider = new TokenProvider();
